/**
 * 静岡県の背景ツアー「静岡めぐり」コンテンツ。
 *
 * 軸は季節。5月の三保松原から始めて、1年かけて翌年4月の來宮神社へ戻る。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - 「日本三大○○」はどの公式資料でも裏が取れなかった。書かない。
 *  - 弁天島のものは「鳥居」ではなく「弁天島観光シンボルタワー」。神社の鳥居ではない。
 *  - 三保松原から見る日の出は富士山の方向ではない（東は駿河湾ごしの伊豆半島）。
 *  - 大室山は徒歩での登山が禁止されている。アクセスはリフトのみ。
 *  - 寸又峡の夢のつり橋は通行時間が決まっていて、季節で変わる。諸元の数値は書かない。
 *  - 日本平夢テラスの展望回廊の長さ・標高は公式で確認できなかったので書かない。
 *  - 來宮神社の大楠の指定年・樹高は公式で確認できなかったので書かない。
 */

import type { StageTour } from '../types';

export const shizuokaSceneryTour: StageTour = {
  scenes: [
    {
      id: 'miho-no-matsubara',
      name: '三保松原',
      area: '静岡市・5月の朝',
      image: '/assets/scenery/shizuoka/scenes/01_miho_no_matsubara.webp',
      hotspots: [
        {
          id: 'miho-kuromatsu',
          x: 0.44,
          y: 0.5,
          title: 'クロマツ約5万本',
          body: '駿河湾に突き出した砂嘴に、およそ5万本のクロマツが4.5キロにわたって生えています。富士山頂の南西およそ45キロ。',
          source: 'https://online.bunka.go.jp/special_content/component/134',
          mapQuery: '三保松原',
        },
        {
          id: 'miho-sekaiisan',
          x: 0.72,
          y: 0.26,
          title: '日本で最初の名勝',
          body: '大正11年（1922年）、日本で初めて名勝に指定されました。2013年6月には「富士山－信仰の対象と芸術の源泉－」の構成資産として世界文化遺産に登録されています。',
          source: 'https://miho-no-matsubara.jp/about/',
          mapQuery: '三保松原 羽衣の松',
        },
        {
          id: 'miho-manner',
          x: 0.22,
          y: 0.66,
          title: '松原のなかは自転車も火も禁止',
          body: '松原の中を自転車で走ったり駐輪したりはできません。バーベキューなど火を使うのも控えるよう案内されています。ゴミ箱はないので持ち帰りを。夜は街灯がほとんどありません。',
          source: 'https://miho-no-matsubara.jp/faq',
          mapQuery: '三保松原文化創造センター',
        },
      ],
    },
    {
      id: 'omuroyama',
      name: '大室山',
      area: '伊東市・6月の午前',
      image: '/assets/scenery/shizuoka/scenes/02_omuroyama.webp',
      hotspots: [
        {
          id: 'omuro-suribachi',
          x: 0.5,
          y: 0.4,
          title: '4000年前の火山',
          body: 'およそ4000年前に活動した標高580メートルの、伊豆半島で最大の単性火山です。底面の直径1キロメートルの大型の臼型スコリア丘で、山頂には直径250メートルの噴火口があります。2010年8月5日に国の天然記念物に指定されました。',
          source:
            'https://www.city.ito.shizuoka.jp/gyosei/soshikikarasagasu/shogaigakushuka/bunka_supotsu/2/2/1/2426.html',
          mapQuery: '大室山',
        },
        {
          id: 'omuro-lift',
          x: 0.24,
          y: 0.6,
          title: '歩いては登れません',
          body: '国の天然記念物で、山体の保護のため徒歩での登山は禁止されています。山頂へはリフトで上がります。古くからの「山焼き」の伝統行事によって、椀を伏せたような山の形が保たれています。',
          source: 'https://izugeopark.org/geosites/omuroyama/',
          mapQuery: '大室山登山リフト',
        },
      ],
    },
    {
      id: 'shiraito-no-taki',
      name: '白糸ノ滝',
      area: '富士宮市・7月の昼',
      image: '/assets/scenery/shizuoka/scenes/03_shiraito_no_taki.webp',
      hotspots: [
        {
          id: 'shiraito-yusui',
          x: 0.5,
          y: 0.42,
          title: '絶壁から数百の白い糸',
          body: '高さ約20メートル、幅150メートル以上にわたる湾曲した絶壁から、大小数百の滝が白い糸を垂らしたように流れ落ちます。富士山麓に降った雨水が、水を通す新富士火山層と水を通さない古富士火山層の境目から湧き出しています。',
          source: 'https://www.city.fujinomiya.lg.jp/1015150000/p001607.html',
          mapQuery: '白糸の滝 富士宮',
        },
        {
          id: 'shiraito-suion',
          x: 0.76,
          y: 0.28,
          title: '一年中12℃',
          body: '水温は年間を通して12℃、毎秒1.5トンの水が流れ出ているといわれます。1936年に国の名勝及び天然記念物に指定され、世界遺産「富士山」の構成資産でもあります。',
          source: 'https://www.city.fujinomiya.lg.jp/1015150000/p001607.html',
          mapQuery: '音止の滝',
        },
      ],
    },
    {
      id: 'kadowaki-tsuribashi',
      name: '城ヶ崎海岸 門脇つり橋',
      area: '伊東市・8月の昼',
      image: '/assets/scenery/shizuoka/scenes/04_kadowaki_tsuribashi.webp',
      hotspots: [
        {
          id: 'kadowaki-hashi',
          x: 0.5,
          y: 0.44,
          title: '全長48メートル、高さ23メートル',
          body: '全長48メートル、高さ約23メートルの、スリル満点のつり橋です。すぐそばの門脇埼灯台の展望台からは、伊豆七島や天城連山を望めます。',
          source: 'https://itospa.com/spot/detail_54002.html',
          mapQuery: '門脇つり橋',
        },
        {
          id: 'kadowaki-yogan',
          x: 0.76,
          y: 0.6,
          title: 'この地面は大室山から流れてきた',
          body: 'およそ4000年前の大室山の噴火で流れ出た溶岩が、海の一部を埋めて新しい大地を作りました。橋からは、溶岩が冷えて縮むときにできる柱状節理が見えます。',
          source: 'https://izugeopark.org/geosites/kadowakizaki/',
          mapQuery: '城ヶ崎海岸',
        },
      ],
    },
    {
      id: 'nihondaira',
      name: '日本平夢テラス',
      area: '静岡市・9月の午後',
      image: '/assets/scenery/shizuoka/scenes/05_nihondaira.webp',
      hotspots: [
        {
          id: 'nihondaira-kigumi',
          x: 0.5,
          y: 0.36,
          title: '県産ひのきの木組み',
          body: '2018年11月にオープンした展望施設。設計は隈研吾建築都市設計事務所で、県産ひのきの伝統的な木組みが屋根まわりの見どころです。',
          source:
            'https://www.pref.shizuoka.jp/kurashikankyo/kenchiku/kokyokenchiku/1003515/1044753/1029174.html',
          mapQuery: '日本平夢テラス',
        },
        {
          id: 'nihondaira-meisho',
          x: 0.76,
          y: 0.28,
          title: '1959年に国の名勝',
          body: '日本平は標高307メートル、有度丘陵の頂上部とその一帯を指します。清水港・三保松原・その向こうの富士山、南に久能山と伊豆半島から御前崎までの駿河湾、北西に赤石山系が見渡せます。',
          source: 'https://online.bunka.go.jp/heritages/detail/172195',
          relatedItemId: 'sakuraebi-kakiage-don',
          mapQuery: '日本平',
        },
      ],
    },
    {
      id: 'yume-no-tsuribashi',
      name: '寸又峡 夢のつり橋',
      area: '川根本町・10月の夕',
      image: '/assets/scenery/shizuoka/scenes/06_yume_no_tsuribashi.webp',
      hotspots: [
        {
          id: 'yumetsuri-mizu',
          x: 0.5,
          y: 0.46,
          title: 'エメラルドグリーンの湖に架かる',
          body: 'エメラルドグリーンの湖に架かる吊り橋です。一度に10人しか渡れず、一方通行で渡ります。',
          source: 'https://okuooi.gr.jp/outdoor/details.php?id=120',
          mapQuery: '寸又峡 夢の吊橋',
        },
        {
          id: 'yumetsuri-jikan',
          x: 0.24,
          y: 0.64,
          title: '渡れる時間が決まっている',
          body: '夢のつり橋（プロムナードコース）は、早朝から日没までの時間帯しか通れません。寸又峡温泉からは歩いておよそ25分です。',
          source: 'https://www.town.kawanehon.shizuoka.jp/soshiki/kankoshoko/faq00/5966.html',
          mapQuery: '寸又峡温泉',
        },
      ],
    },
    {
      id: 'sengen-taisha',
      name: '富士山本宮浅間大社',
      area: '富士宮市・11月の夕',
      image: '/assets/scenery/shizuoka/scenes/07_sengen_taisha.webp',
      hotspots: [
        {
          id: 'sengen-honden',
          x: 0.5,
          y: 0.34,
          title: 'ほかに例のない二層の本殿',
          body: '1606年に徳川家康の庇護のもとで造営されました。二層構造の「浅間造り」という、国内でほかに例のない形です。',
          source: 'https://online.bunka.go.jp/special_content/component/111',
          mapQuery: '富士山本宮浅間大社',
        },
        {
          id: 'sengen-wakutama',
          x: 0.25,
          y: 0.52,
          title: '1日14万立方メートルの湧水',
          body: '境内の湧玉池には、1日平均14万立方メートルという豊富な湧水がわき出ています。1779年には幕府の裁許にもとづき、富士山の八合目以上の支配権が認められました。',
          source: 'https://online.bunka.go.jp/special_content/component/111',
          mapQuery: '湧玉池',
        },
      ],
    },
    {
      id: 'bentenjima',
      name: '弁天島のシンボルタワー',
      area: '浜松市・12月の夕',
      image: '/assets/scenery/shizuoka/scenes/08_bentenjima.webp',
      hotspots: [
        {
          id: 'bentenjima-tower',
          x: 0.5,
          y: 0.42,
          title: '観光協会が建てたタワー',
          body: '1973年8月、舞阪町観光協会が「いかり瀬」に建てた弁天島観光シンボルタワーです。冬至前後に夕日がタワーの中に入る現象は、写真愛好家のシャッタースポットとして注目されています。',
          source: 'https://www.city.hamamatsu.shizuoka.jp/koho2/100ayumi/1973-1.html',
          mapQuery: '弁天島海浜公園',
        },
        {
          id: 'bentenjima-yuhi',
          x: 0.32,
          y: 0.52,
          title: '冬至の前後に、なかへ沈む',
          body: '冬至の前後1か月ごろ、夕日がタワーの中に沈む光景を狙えます。JR弁天島駅から徒歩1分。',
          source: 'https://hamamatsu-daisuki.net/information/5357/',
          relatedItemId: 'unagi-kabayaki',
          mapQuery: '弁天島 夕日',
        },
      ],
    },
    {
      id: 'kunozan-toshogu',
      name: '久能山東照宮',
      area: '静岡市・1月の朝',
      image: '/assets/scenery/shizuoka/scenes/09_kunozan_toshogu.webp',
      hotspots: [
        {
          id: 'kunozan-shaden',
          x: 0.5,
          y: 0.34,
          title: '総漆塗り、極彩色',
          body: '徳川家康の遺命により遺骸を久能山に埋葬し、その地に創建されました。社殿は元和2年5月に着工し、わずか1年7か月で造営されています。権現造・総漆塗・極彩色の社殿は、江戸初期の代表的建造物として国宝に指定されています。',
          source: 'https://www.toshogu.or.jp/about/vanerable.php',
          mapQuery: '久能山東照宮',
        },
        {
          id: 'kunozan-ishidan',
          x: 0.24,
          y: 0.58,
          title: '17曲り1,159段',
          body: '山下の石鳥居から本殿前まで、17回折れ曲がる1,159段の石段。昭和32年に日本平ロープウェイができるまでは、この表参道が唯一の参拝路でした。',
          source: 'https://www.toshogu.or.jp/worship/precincts.php',
          mapQuery: '久能山 表参道',
        },
      ],
    },
    {
      id: 'kinomiya-ookusu',
      name: '來宮神社の大楠',
      area: '熱海市・4月の朝',
      image: '/assets/scenery/shizuoka/scenes/10_kinomiya_ookusu.webp',
      hotspots: [
        {
          id: 'ookusu-miki',
          x: 0.5,
          y: 0.44,
          title: '幹の太さ24メートル',
          body: '樹齢二千百年といわれるクスノキで、幹の太さは24メートルあります。国の天然記念物にも指定されています。',
          source: 'https://www.ataminews.gr.jp/spot/115',
          mapQuery: '來宮神社 大楠',
        },
        {
          id: 'ookusu-densetsu',
          x: 0.76,
          y: 0.3,
          title: '一周にまつわる二つの言い伝え',
          body: '幹を1周まわると寿命が1年延びるという伝説と、心に願いを秘めながら1周すると願いがかなうという伝説が、神社に伝わっています。',
          source: 'https://kinomiya.or.jp/top/overview/ookusu/',
          mapQuery: '來宮神社',
        },
      ],
    },
  ],
};
